import { useCompanyServices } from '../hooks/useCompanyServices';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartSidebar from '../components/SmartSidebar';
import { MdFilterList, MdRefresh, MdSearch, MdClose, MdVisibility, MdDownload, MdAdd } from 'react-icons/md';
import api from '../services/api';
import { getCurrentUser } from '../services/authService';

const filterOptions = ['Pending', 'In Printing', 'In Transit', 'Delivered', 'Cancelled', 'Reset / Show All'];

const ADMIN_ROLES = ['super_admin', 'crm_user'];

const statusConfig = {
  'Pending': { color: '#f97316', bg: '#ffedd5' },
  'Booked': { color: '#068BC9', bg: '#e0f2fe' },
  'In Printing': { color: '#8b5cf6', bg: '#ede9fe' },
  'In Transit': { color: '#1d4ed8', bg: '#dbeafe' },
  'Delivered': { color: '#22c55e', bg: '#dcfce7' },
  'Cancelled': { color: '#ef4444', bg: '#fee2e2' },
};

const SERVICE_NOT_AVAILABLE = () => (
  <div className="flex min-h-screen bg-gray-50 items-center justify-center">
    <div className="text-center">
      <p className="text-4xl mb-4">🔒</p>
      <h2 className="text-lg font-bold text-gray-800 mb-2">Service Not Available</h2>
      <p className="text-sm text-gray-400">This service is not enabled for your company. Please contact ProDiligix support.</p>
    </div>
  </div>
);

export default function StampPaper() {
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const { isEnabled, loading: serviceLoading } = useCompanyServices();
  const [showFilter, setShowFilter] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Latest');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [stampData, setStampData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const user = getCurrentUser();
  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  useEffect(() => {
    const fetchStampPapers = async () => {
      try {
        const response = await api.get('/api/stamp-paper');
        setStampData(response.data);
      } catch (error) {
        console.error('Failed to fetch stamp papers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStampPapers();
  }, []);

  const filtered = [...stampData]
    .filter(o =>
      ((o.serviceRequestId || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (o.firstPartyName || '').toLowerCase().includes(searchText.toLowerCase())) &&
      (!statusFilter || o.status === statusFilter)
    )
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const totalPages = rowsPerPage === 'all' ? 1 : Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated = rowsPerPage === 'all' ? filtered : filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50 items-center justify-center">
        <p className="text-gray-400 text-sm">Loading stamp papers...</p>
      </div>
    );
  }

  const isClientRole = ['company_user', 'company_admin', 'company_crm_user'].includes(user?.role);
  if (isClientRole && !serviceLoading && !isEnabled('stampPaper')) {
    return (
      <div className="flex min-h-screen bg-gray-50 items-center justify-center">
        <div className="text-center">
          <p className="text-4xl mb-4">🔒</p>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Service Not Available</h2>
          <p className="text-sm text-gray-400">This service is not enabled for your company. Please contact ProDiligix support.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-screen bg-gray-50">
      <SmartSidebar onToggle={setSidebarExpanded} />

      <div
        className="flex-1 transition-all duration-300 min-h-screen bg-gray-50"
        style={{ marginLeft: sidebarExpanded ? '240px' : '64px' }}>

        {/* Topbar */}
        <div className="bg-white border-b border-gray-100 px-6 py-3 flex justify-between items-center sticky top-0 z-40">
          <div>
            <p className="text-gray-400 text-xs">Services</p>
            <h1 className="text-base font-bold text-gray-800">Stamp Paper Procurement Management</h1>
          </div>
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
            <MdSearch size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID or Party Name..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="bg-transparent text-sm outline-none w-56 text-gray-600"
            />
            {searchText && (
              <MdClose size={14} className="text-gray-400 cursor-pointer" onClick={() => setSearchText('')} />
            )}
          </div>
        </div>

        <div className="p-5">

          {/* Stat cards */}
          <div className="grid grid-cols-4 gap-4 mb-5">
            {[
              { label: 'Total Requests', value: stampData.length, color: '#068BC9' },
              { label: 'Delivered', value: stampData.filter(o => o.status === 'Delivered').length, color: '#22c55e' },
              { label: 'In Transit', value: stampData.filter(o => o.status === 'In Transit').length, color: '#1d4ed8' },
              { label: 'Pending', value: stampData.filter(o => o.status === 'Pending').length, color: '#f97316' },
            ].map((card, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <p className="text-xs text-gray-400 mb-1">{card.label}</p>
                <p className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</p>
              </div>
            ))}
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative">
              <button
                onClick={() => setShowFilter(!showFilter)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#068BC9' }}>
                <MdFilterList size={18} />
                Add Filter
                <span className="ml-1">{showFilter ? '▲' : '▼'}</span>
              </button>
              {showFilter && (
                <div className="absolute top-10 left-0 bg-white rounded-xl shadow-lg border border-gray-100 z-50 py-2 w-52">
                  <p className="text-xs text-gray-400 px-4 py-1 font-medium uppercase tracking-wider">Select Filter Type</p>
                  {filterOptions.map((opt, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        setActiveFilter(opt);
                        setShowFilter(false);
                        if (opt === 'Reset / Show All') {
                          setStatusFilter('');
                        } else if (['Pending', 'In Printing', 'In Transit', 'Delivered', 'Cancelled'].includes(opt)) {
                          setStatusFilter(opt);
                        } else {
                          setStatusFilter('');
                        }
                      }}
                      className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 text-sm"
                      style={{ color: opt === 'Reset / Show All' ? '#ef4444' : '#374151' }}>
                      {opt}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
              <span className="text-xs font-medium" style={{ color: '#068BC9' }}>
                {activeFilter}: {filtered.length}
              </span>
              <MdClose size={14} className="text-gray-400 cursor-pointer" onClick={() => setActiveFilter('Latest')} />
            </div>

            <span className="text-sm text-gray-500">({filtered.length})</span>

            <button className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <MdRefresh size={18} className="text-gray-400" />
            </button>

            {!isAdmin && (
              <button
                onClick={() => navigate('/stamp-paper/new')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#22c55e' }}>
                <MdAdd size={18} />
                Book A Stamp Paper
              </button>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="w-full min-w-max">
              <thead>
                <tr className="border-b border-gray-100">
                  {[
                    'Service Request ID', '1st Party Name', '2nd Party Name',
                    'Denomination', 'Quantity', 'Total Charges',
                    'Status', 'Request Date', 'Delivery Date', 'Actions'
                  ].map((col, i) => (
                    <th key={i} className="text-left text-xs text-gray-400 font-medium px-4 py-3 whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-10 text-gray-400 text-sm">
                      No stamp paper bookings found
                    </td>
                  </tr>
                ) : (
                  paginated.map((order, i) => {
                    const s = statusConfig[order.status] || { color: '#9ca3af', bg: '#f3f4f6' };
                    return (
                      <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium whitespace-nowrap cursor-pointer hover:underline"
                          style={{ color: '#068BC9' }}
                          onClick={() => navigate(`/stamp-paper/${order.id}`)}>
                          {order.serviceRequestId}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{order.firstPartyName}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{order.secondPartyName}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">₹{order.denomination}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{order.quantity}</td>
                        <td className="px-4 py-3 text-xs font-semibold whitespace-nowrap" style={{ color: '#068BC9' }}>
                          ₹{order.totalCharges}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-xs font-medium px-2 py-1 rounded-full"
                            style={{ color: s.color, backgroundColor: s.bg }}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                          {order.createdAt ? order.createdAt.split('T')[0] : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                          {order.deliveryDate ? order.deliveryDate.split('T')[0] : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                if (order.scannedCopy) {
                                  try {
                                    const files = JSON.parse(order.scannedCopy);
                                    if (files.length > 0) window.open('https://api.prodiligix.com' + files[0], '_blank');
                                  } catch(e) {
                                    window.open('https://api.prodiligix.com' + order.scannedCopy, '_blank');
                                  }
                                } else {
                                  alert('Stamp paper not uploaded yet');
                                }
                              }}
                              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                              style={{ color: '#068BC9', backgroundColor: '#e0f2fe' }}>
                              <MdVisibility size={14} />
                              View
                            </button>
                            {order.scannedCopy ? (
                              <span className="text-xs font-medium px-3 py-1.5 rounded-lg" style={{ color: '#22c55e', backgroundColor: '#dcfce7' }}>
                                Stamp Paper ✓
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">No Doc</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Rows per page:</span>
                <select value={rowsPerPage} onChange={e => { setRowsPerPage(e.target.value === 'all' ? 'all' : parseInt(e.target.value)); setCurrentPage(1); }}
                  className="text-xs border border-gray-200 rounded px-2 py-1 text-gray-600 outline-none">
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value="all">All</option>
                </select>
                <span className="text-xs text-gray-500">Showing {rowsPerPage === 'all' ? 1 : (currentPage - 1) * rowsPerPage + 1}-{rowsPerPage === 'all' ? filtered.length : Math.min(currentPage * rowsPerPage, filtered.length)} of {filtered.length}</span>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && <span className="px-1 text-gray-300 text-xs">...</span>}
                      <button onClick={() => setCurrentPage(p)}
                        className="w-8 h-8 rounded-lg text-xs font-medium transition-colors"
                        style={{ backgroundColor: currentPage === p ? '#068BC9' : 'transparent', color: currentPage === p ? '#fff' : '#6b7280' }}>
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}